// ** MUI Imports
import Drawer from '@mui/material/Drawer';

// ** Type Import
import type { LayoutProps } from '../../../../types';

import clsx from 'clsx';

interface Props {
  navWidth: number;
  navHover: boolean;
  navVisible: boolean;
  collapsedNavWidth: number;
  navigationBorderWidth: number;
  hidden: LayoutProps['hidden'];
  settings: LayoutProps['settings'];
  children: LayoutProps['children'];
  setNavHover: (values: boolean) => void;
  setNavVisible: (value: boolean) => void;
  navMenuProps: LayoutProps['verticalLayoutProps']['navMenu']['componentProps'];
}

const NavDrawer = (props: Props) => {
  'use memo';

  // ** Props
  const {
    hidden,
    children,
    navHover,
    navWidth,
    settings,
    navVisible,
    setNavHover,
    setNavVisible,
    collapsedNavWidth,
  } = props;

  // ** Vars
  const { navCollapsed } = settings;

  let flag = true;

  // Drawer Props for Mobile & Tablet screens
  const MobileDrawerProps = {
    open: navVisible,
    onClose: () => setNavVisible(false),
    ModalProps: {
      keepMounted: true, // Better open performance on mobile.
    },
  };

  // Drawer Props for Laptop & Desktop screens
  const DesktopDrawerProps = {
    open: true,
    onMouseEnter: () => {
      // Declared flag to resolve first time flicker issue while trying to collapse the menu
      if (flag || navCollapsed) {
        setNavHover(true);
        flag = false;
      }
    },
    onMouseLeave: () => {
      if (navCollapsed) {
        setNavHover(false);
      }
    },
  };

  return (
    <Drawer
      className={clsx(
        'layout-vertical-nav',
        navCollapsed && !navHover ? 'nav-collapsed' : undefined,
      )}
      variant={hidden ? 'temporary' : 'permanent'}
      {...(hidden ? { ...MobileDrawerProps } : { ...DesktopDrawerProps })}
      slotProps={{
        paper: {
          sx: {
            overflowX: 'hidden',
            width: navCollapsed && !navHover ? collapsedNavWidth : navWidth,
            ...(!hidden && navCollapsed && navHover ? { boxShadow: 9 } : {}),
            paddingTop: 'env(safe-area-inset-top)',
            backgroundColor: 'background.menu',
            transition: 'width .25s ease-in-out',
            '& .hide-when-collapsed': {
              opacity: navCollapsed && !navHover ? 0 : 1,
              transition: 'opacity .25s ease-in-out',
            },
          },
        },
      }}
      sx={{
        width: navCollapsed ? collapsedNavWidth : navWidth,
      }}
    >
      {children}
    </Drawer>
  );
};

export default NavDrawer;
